import { Helmet } from "react-helmet-async";

const SITE_NAME = "Eliada Sports Agency";
const BASE_URL = "https://eliadasportsagency.com";
const DEFAULT_IMAGE = `${BASE_URL}/opengraph.jpg`;
const DEFAULT_DESCRIPTION =
  "Eliada Sports Agency is an international sports management and talent development organization connecting athletes with professional opportunities across Africa, Europe, and North America.";

const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Eliada Sports Agency",
  url: BASE_URL,
  logo: `${BASE_URL}/images/logo.png`,
  description: DEFAULT_DESCRIPTION,
  contactPoint: {
    "@type": "ContactPoint",
    email: "contact@eliadasportsagency.com",
    telephone: "+49 15770 904476",
    contactType: "customer service",
  },
  address: {
    "@type": "PostalAddress",
    streetAddress: "Unterseilberg 9",
    addressLocality: "Grainet",
    postalCode: "94143",
    addressRegion: "Bayern",
    addressCountry: "DE",
  },
  sameAs: [
    "https://facebook.com/eliadasportsagency",
    "https://instagram.com/eliadasportsagency",
    "https://twitter.com/eliadasports",
    "https://linkedin.com/company/eliadasportsagency",
    "https://youtube.com/@eliadasportsagency",
    "https://tiktok.com/@eliadasportsagency",
  ],
};

interface SeoHeadProps {
  title?: string;
  description?: string;
  image?: string;
  path?: string;
  type?: "website" | "article";
  noIndex?: boolean;
}

export function SeoHead({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  path = "",
  type = "website",
  noIndex = false,
}: SeoHeadProps) {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} – Developing Global Sports Talents`;
  const canonicalUrl = `${BASE_URL}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {!noIndex && <meta name="robots" content="index, follow" />}
      <meta name="author" content={SITE_NAME} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@eliadasports" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(ORG_SCHEMA)}</script>
    </Helmet>
  );
}
